import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  LinearProgress,
} from '@mui/material';
import { CloudUpload, CheckCircle, ArrowBack } from '@mui/icons-material';
import { apiService, CreateFileResponse, CreateFileError } from '../services/api';
import { listProjects } from '../services/projectApi';
import MultiFileUpload from '../components/Files/MultiFileUpload';
import { 
  getExecutionPlan, 
  ExecutionPlanResponse,
  getActionCompletionStatus,
  CompletionStatus,
  getActionAssignments,
  ActionAssignment,
} from '../services/planningExecutionApi';
import { ProjectsListResponse, UploadedFile, DocumentAssociation } from '../types';
import { getUserAccessToken } from '../utils/tokenStorage';
import { useAuth } from '../contexts/AuthContext';
import { isInternalUser } from '../utils/sharePointLinkUtils';
import {
  dismissAfterActionForm,
  EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE,
  shouldNavigateIntoPortfolioAfterAction,
} from '../utils/actionFormNavigation';

interface CreateFileFormData {
  file: File | null;
  projectId: number | null;
  phaseId: number | null;
  taskId: number | null;
  folderPath: string;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

// TODO: Get these from project configuration/API endpoint
// For now, hard-coded for POC - replace with actual values from project configuration in production
const HARDCODED_SITE_ID = '7de7c338-76a1-472e-8f6a-f2b95c3bb7d5'; // relevantcrm.sharepoint.com siteId
// libraryName removed - backend now auto-detects from airport's default library

const CreateActionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<CreateFileFormData>({
    file: null,
    projectId: null,
    phaseId: null,
    taskId: null,
    folderPath: '',
  });
  const [projects, setProjects] = useState<ProjectsListResponse['projects']>([]);
  const [executionPlan, setExecutionPlan] = useState<ExecutionPlanResponse | null>(null);
  const [_loadingProjects, setLoadingProjects] = useState(false);
  const [loadingProjectDetails, setLoadingProjectDetails] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<CreateFileResponse | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [tokenExpired, setTokenExpired] = useState(false);
  const [queryParamsProcessed, setQueryParamsProcessed] = useState(false);
  const [completionStatus, setCompletionStatus] = useState<CompletionStatus | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [_assignedUsers, setAssignedUsers] = useState<ActionAssignment['user'][]>([]);
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [requiredDocumentCount, setRequiredDocumentCount] = useState<number | null>(null);
  const [_uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [actionDocuments, setActionDocuments] = useState<DocumentAssociation[]>([]);

  // Read query parameters from URL
  useEffect(() => {
    const urlProjectId = searchParams.get('projectId');
    // @ts-ignore - intentionally unused
    const _urlActionId = searchParams.get('actionId');
    
    if (urlProjectId && !queryParamsProcessed) {
      const projectIdNum = parseInt(urlProjectId, 10);
      if (!isNaN(projectIdNum) && projectIdNum > 0) {
        console.log('CreateActionPage: Found projectId in URL:', projectIdNum);
        setFormData(prev => ({
          ...prev,
          projectId: projectIdNum,
        }));
        setQueryParamsProcessed(true);
      }
    }
  }, [searchParams, queryParamsProcessed]);

  useEffect(() => {
    loadProjects();
  }, []);

  // Load execution plan when project is selected
  useEffect(() => {
    if (formData.projectId) {
      loadProjectDetails(formData.projectId);
    } else {
      // Reset when project is cleared
      setFormData(prev => ({
        ...prev,
        phaseId: null,
        taskId: null,
        folderPath: '',
      }));
      setExecutionPlan(null);
    }
  }, [formData.projectId]);

  // Auto-select phase and task when actionId is provided in URL and execution plan is loaded (Sprint H4: also set task when taskId missing)
  // Also check action type and redirect REVISE actions to the revision upload page
  useEffect(() => {
    const urlActionId = searchParams.get('actionId');
    const needTask = urlActionId && executionPlan && (!formData.phaseId || !formData.taskId);
    if (needTask) {
      const actionIdNum = parseInt(urlActionId, 10);
      if (!isNaN(actionIdNum) && actionIdNum > 0) {
        console.log('CreateActionPage: Looking for actionId in execution plan:', actionIdNum);
        
        // Search through phases, tasks, and actions to find the matching action
        for (const phase of executionPlan.phases) {
          if (phase.tasks) {
            for (const task of phase.tasks) {
              if (task.actions) {
                const action = task.actions.find(a => a.actionId === actionIdNum);
                if (action) {
                  console.log('CreateActionPage: Found action, checking type:', {
                    actionId: actionIdNum,
                    actionType: action.actionType,
                    phaseId: phase.phaseId,
                    taskId: task.taskId,
                  });
                  
                  // Redirect non-CREATE actions to their appropriate pages
                  if (action.actionType === 'REVISE') {
                    console.log('CreateActionPage: REVISE action detected, redirecting to revision upload page');
                    navigate(`/revision/upload?actionId=${actionIdNum}&projectId=${formData.projectId || executionPlan.projectId}`);
                    return;
                  } else if (action.actionType === 'REVIEW') {
                    console.log('CreateActionPage: REVIEW action detected, redirecting to review acknowledge page');
                    // REVIEW actions need a token, but we can redirect with actionId for logged-in users
                    // The backend will need to support this, or we generate a token
                    navigate(`/review/acknowledge?actionId=${actionIdNum}&projectId=${formData.projectId || executionPlan.projectId}`);
                    return;
                  } else if (action.actionType === 'APPROVE') {
                    console.log('CreateActionPage: APPROVE action detected, redirecting to approval page');
                    // APPROVE actions need a token, but we can redirect with actionId for logged-in users
                    navigate(`/approval/approve?actionId=${actionIdNum}&projectId=${formData.projectId || executionPlan.projectId}`);
                    return;
                  }
                  
                  // For CREATE actions, set phase and task so Task section is visible (H4/H5)
                  if (action.actionType === 'CREATE') {
                    setFormData(prev => ({
                      ...prev,
                      phaseId: phase.phaseId,
                      taskId: task.taskId,
                    }));
                    return;
                  }
                  
                  // Unknown action type - show error
                  console.warn('CreateActionPage: Unknown action type:', action.actionType);
                  setError(`This page only supports CREATE actions. Action type "${action.actionType}" is not supported here.`);
                  return;
                }
              }
            }
          }
        }
        console.warn('CreateActionPage: actionId not found in execution plan:', actionIdNum);
      }
    }
  }, [executionPlan, searchParams, formData.phaseId, formData.taskId, formData.projectId, navigate]);

  // Set folder path from action document's targetFolderPath if available
  // Note: Backend will use targetFolderPath from ActionDocuments when uploading, so folderPath is only a fallback
  useEffect(() => {
    // First priority: Use targetFolderPath from action documents
    if (actionDocuments.length > 0) {
      const firstDocument = actionDocuments.find(doc => !doc.spItemId && !doc.localFileId);
      if (firstDocument?.targetFolderPath) {
        // targetFolderPath is already in the correct format (e.g., "/Designs/Phase1")
        // Backend will use this directly from ActionDocuments, but we set it here for fallback
        setFormData(prev => ({
          ...prev,
          folderPath: firstDocument.targetFolderPath || '',
        }));
        return; // Exit early if we found targetFolderPath
      }
    }
    
    // Fallback: If no targetFolderPath, use phase name as relative path (no leading slash)
    // Backend expects relative paths when folderPath is used as fallback.
    // E1: Do not clear taskId when set from actionId URL so Task section stays visible.
    if (executionPlan && formData.phaseId) {
      const phase = executionPlan.phases.find(p => p.phaseId === formData.phaseId);
      if (phase) {
        const phaseName = sanitizeFolderName(phase.name);
        setFormData(prev => ({ ...prev, folderPath: phaseName }));
      }
    } else if (!formData.phaseId) {
      setFormData(prev => ({
        ...prev,
        folderPath: '',
        taskId: null,
      }));
    }
  }, [formData.phaseId, executionPlan, actionDocuments]);

  // Check completion status and load action details when actionId is available
  // Only run after we have executionPlan so we can skip APPROVE/REVIEW (no completion-status for those)
  useEffect(() => {
    const urlActionId = searchParams.get('actionId');
    if (!urlActionId) {
      setCompletionStatus(null);
      setAssignedUsers([]);
      setUploadDisabled(false);
      setRequiredDocumentCount(null);
      return;
    }

    const actionIdNum = parseInt(urlActionId, 10);
    if (isNaN(actionIdNum) || actionIdNum <= 0) {
      return;
    }

    // If we have the plan, skip status/assignments for APPROVE/REVIEW (redirect will happen; avoid 403)
    const actionTypeFromPlan = executionPlan?.phases?.flatMap(p => p.tasks ?? [])
      .flatMap(t => t.actions ?? [])
      .find(a => a.actionId === actionIdNum)?.actionType;
    if (actionTypeFromPlan === 'APPROVE' || actionTypeFromPlan === 'REVIEW') {
      return;
    }

    const checkStatus = async () => {
      setIsCheckingStatus(true);
      try {
        // Completion status is only for CREATE/REVISE actions
        const isCreateOrReviseAction = executionPlan?.phases.some(phase =>
          phase.tasks?.some(task =>
            task.actions?.some(a => a.actionId === actionIdNum && (a.actionType === 'CREATE' || a.actionType === 'REVISE'))
          )
        );
        
        if (isCreateOrReviseAction) {
          try {
            const status = await getActionCompletionStatus(actionIdNum);
            setCompletionStatus(status);
            
            // If already completed, disable upload
            if (status.isCompleted) {
              setUploadDisabled(true);
            }
          } catch (err) {
            // Completion status is optional, only log for CREATE/REVISE actions
            console.debug('Error checking completion status (CREATE/REVISE action only):', err);
          }
        }

        // Load assigned users (for all action types except APPROVE/REVIEW when we're redirecting)
        try {
          const assignments = await getActionAssignments(actionIdNum);
          setAssignedUsers(assignments.map(a => a.user));
        } catch (err) {
          console.error('Error loading assigned users:', err);
        }

        // Load action details to get requiredDocumentCount (for CREATE actions)
        try {
          const action = await apiService.getAction(actionIdNum);
          if (action.requiredDocumentCount) {
            setRequiredDocumentCount(action.requiredDocumentCount);
          }
        } catch (err) {
          console.error('Error loading action details:', err);
        }

        // Load action documents to get document titles and descriptions (for CREATE actions)
        try {
          const documents = await apiService.getActionDocuments(actionIdNum);
          setActionDocuments(documents);
        } catch (err) {
          console.error('Error loading action documents:', err);
        }
      } catch (error) {
        console.error('Error in checkStatus:', error);
        // Don't set error state - this is optional
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkStatus();
  }, [searchParams, executionPlan]);

  const sanitizeFolderName = (name: string): string => {
    // Remove or replace invalid characters for SharePoint folder names
    // Invalid: < > : " | ? * \ / and leading/trailing spaces
    return name
      .replace(/[<>:"|?*\\/]/g, '-')
      .replace(/^[\s.]+|[\s.]+$/g, '')
      .trim();
  };

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await listProjects({ page: 1, pageSize: 100 });
      setProjects(response.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadProjectDetails = async (projectId: number) => {
    try {
      setLoadingProjectDetails(true);
      setError(null);

      // Load execution plan to get phases and tasks
      try {
        const plan = await getExecutionPlan(projectId);
        setExecutionPlan(plan);
        
        // Check if execution plan has phases
        if (!plan.phases || plan.phases.length === 0) {
          setError('This project does not have any phases configured. Please create phases in the project before uploading files.');
        } else {
          console.log('CreateActionPage: Execution plan loaded successfully:', {
            projectId,
            phaseCount: plan.phases.length,
          });
        }
      } catch (err) {
        // Execution plan might not exist yet (project still in planning)
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(`Execution plan not found. The project may still be in planning phase. Please lock the project first. Error: ${errorMessage}`);
        setExecutionPlan(null);
        setFormData(prev => ({
          ...prev,
          phaseId: null,
          taskId: null,
          folderPath: '',
        }));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load project details';
      setError(errorMessage);
      console.error('Error loading project details:', err);
    } finally {
      setLoadingProjectDetails(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.file) {
      errors.file = 'File is required';
    } else if (formData.file.size > MAX_FILE_SIZE) {
      errors.file = `File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`;
    }

    if (!formData.projectId || formData.projectId <= 0) {
      errors.projectId = 'Valid project is required';
    }

    // libraryName validation removed - backend auto-detects from airport

    if (!formData.phaseId) {
      errors.phaseId = 'Phase selection is required';
    }

    // Folder path is auto-generated from phase - ensure it's set
    if (!formData.folderPath || formData.folderPath.trim() === '') {
      errors.folderPath = 'Folder path is required (auto-generated from phase)';
    } else if (!formData.folderPath.startsWith('/')) {
      errors.folderPath = 'Folder path must start with /';
    }


    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        setValidationErrors({
          ...validationErrors,
          file: `File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`,
        });
        return;
      }
      setFormData({ ...formData, file: selectedFile });
      setValidationErrors({ ...validationErrors, file: '' });
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!formData.file || !formData.projectId) {
      return;
    }

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      const userAccessToken = getUserAccessToken();
      
      // Get actionId from URL query parameter if present
      const urlActionId = searchParams.get('actionId');
      const actionId = urlActionId ? parseInt(urlActionId, 10) : null;
      
      // Log authentication status and actionId for debugging
      // Note: Backend uses app-only authentication for SharePoint operations
      // userAccessToken is accepted but ignored for SharePoint (may be used for email operations)
      console.log('[Create Action] Frontend: Submission details:', {
        hasUserAccessToken: !!userAccessToken,
        actionId: actionId,
        actionIdFromURL: urlActionId,
        projectId: formData.projectId,
        phaseId: formData.phaseId,
        taskId: formData.taskId,
        note: 'Backend uses app-only authentication for SharePoint operations. User token is optional and may be used for email operations only. ActionId ensures the action is marked complete and cascading updates are triggered.'
      });
      
      if (!actionId) {
        console.warn('[Create Action] Frontend: No actionId found in URL query parameters. Action will not be marked as complete automatically.');
      }

      const result = await apiService.createFile(
        formData.file,
        formData.projectId,
        HARDCODED_SITE_ID,
        formData.folderPath.trim(),
        undefined, // recipientEmail removed
        userAccessToken || undefined,
        (progressEvent: { loaded: number; total: number }) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
        actionId || undefined, // Pass actionId from URL query parameter
        // libraryName omitted - backend auto-detects from airport's default library
      );

      // Handle alreadyCompleted response
      if (result.alreadyCompleted && result.completion) {
        // Update completion status
        setCompletionStatus({
          actionId: result.completion.actionId,
          isCompleted: true,
          completedBy: result.completion.completedBy,
          completedAt: result.completion.completedAt,
          details: result.completion.details,
        });
        setUploadDisabled(true);
      }

      setSuccess(result);
    } catch (err: unknown) {
      let errorMessage = err instanceof Error ? err.message : 'Failed to upload file';
      let isTokenExpired = false;
      
      // Get error data from either err.response.data or err.data (interceptor may attach it directly)
      const errAny = err as any;
      const errorData = (errAny?.response?.data || errAny?.data) as CreateFileError;
      const status = errAny?.response?.status || errAny?.status;
      const hasToken = !!getUserAccessToken();
      
      // Handle authentication errors
      // Backend uses app-only authentication for SharePoint operations
      if (status === 401 || status === 500) {
        // Check for app-only token expiration
        if (errorData?.code === 'APP_TOKEN_EXPIRED' || 
            errorData?.code === 'AUTH_ERROR' ||
            errorData?.message?.includes('App-only authentication token expired') ||
            errorData?.message?.includes('app registration credentials')) {
          isTokenExpired = true;
          setTokenExpired(true);
          errorMessage = `App authentication error: ${errorData?.message || 'The server authentication token has expired. Please contact your administrator or try again later.'}`;
        } 
        // Legacy user token expiration (should not occur with app-only auth, but handle for compatibility)
        else if (errorData?.code === 'TOKEN_EXPIRED' || 
                 errorData?.code === 'INVALID_AUTHENTICATION_TOKEN' ||
                 errorData?.message?.includes('expired') || 
                 errorData?.message?.includes('Lifetime validation failed') ||
                 errorData?.message?.includes('token is expired') ||
                 (err instanceof Error && (err.message?.includes('expired') || err.message?.includes('token expired')))) {
          isTokenExpired = true;
          setTokenExpired(true);
          errorMessage = `Authentication error: ${errorData?.message || (err instanceof Error ? err.message : '') || 'Authentication token expired. Please contact your administrator.'}`;
        } else {
          errorMessage = `Authentication failed: ${errorData?.message || (err instanceof Error ? err.message : '') || 'Invalid or expired authentication token'}`;
        }
      } else if (errorData) {
        errorMessage = errorData.message || errorMessage;
        
        // Handle specific error codes
        switch (errorData.code) {
          case 'VALIDATION_ERROR':
            errorMessage = `Validation error: ${errorData.message}`;
            break;
          case 'INVALID_EMAIL':
            errorMessage = `Invalid email: ${errorData.message}`;
            break;
          case 'NO_PROJECTS':
            errorMessage = `No projects found: ${errorData.message}`;
            break;
          case 'FILE_NOT_FOUND':
            errorMessage = `File not found: ${errorData.message}`;
            break;
          case 'ACCESS_DENIED':
            errorMessage = `Access denied: ${errorData.message}`;
            break;
          case 'LIBRARY_NOT_FOUND':
            errorMessage = `Library not found: ${errorData.message}. Please check the library name and try again.`;
            break;
          case 'APP_TOKEN_EXPIRED':
          case 'AUTH_ERROR':
            isTokenExpired = true;
            setTokenExpired(true);
            errorMessage = `App authentication error: ${errorData.message || 'The server authentication token has expired. Please contact your administrator or try again later.'}`;
            break;
          case 'TOKEN_EXPIRED':
          case 'INVALID_AUTHENTICATION_TOKEN':
            // Legacy user token expiration (should not occur with app-only auth)
            isTokenExpired = true;
            setTokenExpired(true);
            errorMessage = `Authentication error: ${errorData.message || 'Authentication token expired. Please contact your administrator.'}`;
            break;
          case 'GRAPH_API_ERROR':
            errorMessage = `SharePoint error: ${errorData.message}`;
            break;
        }
      } else if (err instanceof Error && err.message) {
        // Check error message for token expiration keywords as fallback
        if (err.message.includes('expired') || err.message.includes('token expired') || err.message.includes('authentication')) {
          isTokenExpired = true;
          setTokenExpired(true);
          errorMessage = `Authentication error: ${err.message}. Please contact your administrator if the issue persists.`;
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      console.error('Upload error:', {
        error: err,
        status: status,
        statusText: (err as any)?.response?.statusText || (err as any)?.statusText,
        data: errorData,
        code: errorData?.code,
        hasUserAccessToken: hasToken,
        isTokenExpired: isTokenExpired
      });
    } finally {
      setUploading(false);
    }
  };

  if (success) {
    const projectName = projects.find(p => p.projectId === formData.projectId)?.name;
    const phase = executionPlan?.phases.find(p => p.phaseId === formData.phaseId);
    let task = phase?.tasks?.find(t => t.taskId === formData.taskId);
    // H5: Fallback when taskId missing — derive from action in plan
    if (!task && success.actionId && executionPlan?.phases) {
      for (const p of executionPlan.phases) {
        const t = p.tasks?.find(ta => ta.actions?.some((a: { actionId: number }) => a.actionId === success.actionId));
        if (t) {
          task = t;
          break;
        }
      }
    }
    
    // File size only shown when we have a valid value (avoids "Unknown")
    const fileSizeVal = success.metadata.fileSize || (formData.file ? formData.file.size : 0);
    const fileSizeDisplay = fileSizeVal > 0
      ? `${(fileSizeVal / 1024 / 1024).toFixed(2)} MB`
      : null;

    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              File Uploaded Successfully
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {success.message}
            </Typography>
            {success.alreadyCompleted && success.completion && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> This action was already completed by{' '}
                  {success.completion.completedBy.displayName}{' '}
                  on {new Date(success.completion.completedAt).toLocaleDateString()}.
                  Your file was uploaded successfully, but the action completion was recorded earlier.
                </Typography>
              </Alert>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Project Details
            </Typography>
            <Box sx={{ pl: 2, mb: 3 }}>
               {projectName && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Project:</strong> {projectName}
                </Typography>
               )}
               {phase && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Phase:</strong> {phase.name}
                  </Typography>
                  {phase.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {phase.description}
                    </Typography>
                  )}
                </>
               )}
               {task && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Task:</strong> {task.name}
                  </Typography>
                  {task.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {task.description}
                    </Typography>
                  )}
                </>
               )}
            </Box>

            {actionDocuments.length > 0 && (
              <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Document Information
            </Typography>
                <Box sx={{ pl: 2 }}>
                  {actionDocuments.map((doc, index) => (
                    <Box key={doc.actionDocumentId ?? index} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <strong>Document Title:</strong> {`${doc.documentTitle || doc.fileName || 'Document'} – CREATE`}
                      </Typography>
                      {doc.documentDescription && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Document Instructions:</strong> {doc.documentDescription}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Typography variant="h6" gutterBottom>
              File Details
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>File Name:</strong> {success.fileName}
              </Typography>
              {fileSizeDisplay != null && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>File Size:</strong> {fileSizeDisplay}
                </Typography>
              )}
              {(success.webUrl || success.spItemId) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    if (isInternalUser(user) && success.webUrl) {
                      window.open(success.webUrl, '_blank');
                      return;
                    }
                    if (success.spItemId) {
                      try {
                        const blob = await apiService.downloadFile(success.spItemId, 'SHAREPOINT');
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = success.fileName ?? 'document';
                        a.click();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        console.error('Download failed:', err);
                      }
                    }
                  }}
                >
                  {isInternalUser(user) ? 'View document in SharePoint' : 'Download document'}
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
            {!shouldNavigateIntoPortfolioAfterAction(user) ? (
              <Typography variant="body1" sx={{ fontWeight: 500, textAlign: 'center' }}>
                {EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE}
              </Typography>
            ) : (
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  dismissAfterActionForm(navigate, user, {
                    internalProjectId: formData.projectId ?? undefined,
                  });
                }}
              >
                {formData.projectId ? 'Return to Execution View' : 'Return to Dashboard'}
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            type="button"
            startIcon={<ArrowBack />}
            onClick={() => {
              if (shouldNavigateIntoPortfolioAfterAction(user)) {
                if (formData.projectId) {
                  navigate(`/execution?projectId=${formData.projectId}`);
                } else {
                  navigate('/builder');
                }
              } else {
                dismissAfterActionForm(navigate, user, {});
              }
            }}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
            <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" gutterBottom>
              Upload File
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload a new file and track it in FRAmework
            </Typography>
          </Box>
          <Box sx={{ width: 100 }} /> {/* Spacer for centering */}
        </Box>

        {/* Completion Status Alert */}
        {completionStatus?.isCompleted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Action Already Completed:</strong> This action was already completed by{' '}
              {completionStatus.completedBy?.displayName || completionStatus.completedBy?.email}{' '}
              on {completionStatus.completedAt ? new Date(completionStatus.completedAt).toLocaleDateString() : 'unknown date'}.
              {completionStatus.details?.fileName && (
                <> File: {completionStatus.details.fileName}</>
              )}
            </Typography>
          </Alert>
        )}

        {/* Loading completion status */}
        {isCheckingStatus && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Checking completion status...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }} 
            onClose={() => {
              setError(null);
              setTokenExpired(false);
            }}
          >
            <Typography variant="body2" component="div">
              {error}
              {tokenExpired && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>To fix this:</strong>
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                    This is a server-side authentication issue. Please:
                    <br />
                    1. Wait a moment and try again (the server may refresh its token automatically)
                    <br />
                    2. Contact your administrator to verify app registration credentials are configured correctly
                    <br />
                    3. If the issue persists, the server may need to be restarted to refresh authentication tokens
                  </Typography>
                </Box>
              )}
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Informational Fields - Read-only */}
          {formData.projectId && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Project
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {projects.find(p => p.projectId === formData.projectId)?.name || 'Loading...'}
              </Typography>
            </Box>
          )}

          {formData.phaseId && executionPlan && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Phase
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {executionPlan.phases.find(p => p.phaseId === formData.phaseId)?.name || 'Loading...'}
              </Typography>
              {executionPlan.phases.find(p => p.phaseId === formData.phaseId)?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {executionPlan.phases.find(p => p.phaseId === formData.phaseId)?.description}
                </Typography>
              )}
            </Box>
          )}

          {(() => {
            const urlActionId = searchParams.get('actionId');
            const phase = formData.phaseId && executionPlan ? executionPlan.phases.find(p => p.phaseId === formData.phaseId) : null;
            const taskFromForm = phase?.tasks?.find(t => t.taskId === formData.taskId);
            const taskFromAction = !taskFromForm && urlActionId && phase ? phase.tasks?.find(t => t.actions?.some((a: { actionId: number }) => a.actionId === parseInt(urlActionId, 10))) : null;
            const displayTask = taskFromForm ?? taskFromAction ?? null;
            return displayTask ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Task
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {displayTask.name || 'Loading...'}
                </Typography>
                {displayTask.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {displayTask.description}
                  </Typography>
                )}
              </Box>
            ) : null;
          })()}

          {/* File Upload */}
          {requiredDocumentCount && requiredDocumentCount > 0 && formData.projectId && formData.phaseId ? (
            <Box>
              {/* Display document titles and descriptions above upload control */}
              {actionDocuments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Required Documents
                  </Typography>
                  {actionDocuments
                    .filter(doc => !doc.spItemId && !doc.localFileId) // Show documents that haven't been uploaded yet
                    .map((doc, index) => (
                      <Paper key={doc.actionDocumentId || index} sx={{ p: 2, mb: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {`${doc.documentTitle || doc.fileName || 'Document'} – CREATE`}
                        </Typography>
                        {doc.documentDescription && (
                          <Typography variant="body2" color="text.secondary">
                            {doc.documentDescription}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                </Box>
              )}
              <MultiFileUpload
                requiredCount={requiredDocumentCount}
                onUploadComplete={(files: UploadedFile[]) => {
                  setUploadedFiles(files);
                  // Show success message
                  setSuccess({
                    success: true,
                    message: `Successfully uploaded ${files.length} file(s)`,
                    spItemId: files[0]?.fileId || 0,
                    itemId: '',
                    webUrl: '',
                    fileName: files.map(f => f.fileName).join(', '),
                    metadata: {
                      fileName: files[0]?.fileName || '',
                      fileSize: files.reduce((sum, f) => sum + f.fileSize, 0),
                      mimeType: '',
                      extension: '',
                    },
                    actionId: parseInt(searchParams.get('actionId') || '0', 10),
                  });
                }}
                onUploadProgress={(progress) => {
                  setUploadProgress(progress);
                }}
                actionId={parseInt(searchParams.get('actionId') || '0', 10)}
                projectId={formData.projectId}
                folderPath={formData.folderPath || `/${executionPlan?.phases.find(p => p.phaseId === formData.phaseId)?.name || ''}`}
                siteId={HARDCODED_SITE_ID}
              />
            </Box>
          ) : (
            <Box>
              {requiredDocumentCount === null && searchParams.get('actionId') ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Loading action details...
                </Alert>
              ) : !requiredDocumentCount || requiredDocumentCount === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {searchParams.get('actionId') 
                    ? 'This action does not require multiple documents. Use single file upload below.'
                    : 'Select a project and action to enable multi-file upload, or use single file upload below.'}
                </Alert>
              ) : null}
              <input
                accept="*/*"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<CloudUpload />}
                  disabled={uploading}
                  sx={{ mb: 1 }}
                >
                  {formData.file ? formData.file.name : 'Select File'}
                </Button>
              </label>
              {formData.file && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Selected: {formData.file.name} ({(formData.file.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              )}
              {validationErrors.file && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {validationErrors.file}
                </Typography>
              )}
            </Box>
          )}

          {/* Loading indicator for project details */}
          {loadingProjectDetails && formData.projectId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading project details...
              </Typography>
            </Box>
          )}


          {/* Upload Progress */}
          {uploading && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Uploading... {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {/* Submit Button - Only show for single file upload */}
          {(!requiredDocumentCount || requiredDocumentCount === 0) && (
            <Button
              type="button"
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={uploading || uploadDisabled || !formData.file || !formData.projectId || !formData.phaseId || !formData.folderPath}
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
            >
              {uploading ? 'Uploading...' : uploadDisabled ? 'Action Already Completed' : 'Upload File'}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateActionPage;

